/**
 * Super Admin - Property Owners Management
 * Dedicated page for managing property owners
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import InputAdornment from "@mui/material/InputAdornment";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React examples
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

// API
import { superAdminAPI } from "utils/api";

function PropertyOwnersManagement() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menu, setMenu] = useState(null);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [actionDialog, setActionDialog] = useState({ open: false, action: null });
  const [approvalNotes, setApprovalNotes] = useState("");
  const [approvalType, setApprovalType] = useState("Standard");
  const [editType, setEditType] = useState("Standard");
  const [editNotes, setEditNotes] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [emailVerifiedFilter, setEmailVerifiedFilter] = useState("all");
  const [profileCompleteFilter, setProfileCompleteFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [alertMessage, setAlertMessage] = useState({ type: "", message: "" });

  // Helper function to check if profile is complete
  const isProfileComplete = (owner) => {
    return !!(
      owner.idCard?.url &&
      owner.address &&
      owner.nationality &&
      owner.bankDetails?.bankName &&
      owner.bankDetails?.accountNumber &&
      owner.bankDetails?.accountHolderName
    );
  };

  useEffect(() => {
    fetchPropertyOwners();
  }, [statusFilter]);

  // Apply client-side filters
  useEffect(() => {
    let filtered = [...data];

    // Apply type filter
    if (typeFilter !== "all") {
      filtered = filtered.filter((owner) => (owner.type || "Standard") === typeFilter);
    }

    // Apply email verification filter
    if (emailVerifiedFilter !== "all") {
      const isVerified = emailVerifiedFilter === "verified";
      filtered = filtered.filter((owner) => owner.emailVerified === isVerified);
    }

    // Apply profile completion filter
    if (profileCompleteFilter !== "all") {
      const isComplete = profileCompleteFilter === "complete";
      filtered = filtered.filter((owner) => isProfileComplete(owner) === isComplete);
    }

    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(
        (owner) =>
          owner.fullName?.toLowerCase().includes(query) ||
          owner.email?.toLowerCase().includes(query) ||
          owner.phone?.includes(query) ||
          owner.notes?.toLowerCase().includes(query)
      );
    }

    // Apply date filter
    if (dateFilter !== "all") {
      const now = new Date();
      const filterDate = new Date();

      switch (dateFilter) {
        case "today":
          filterDate.setHours(0, 0, 0, 0);
          filtered = filtered.filter((owner) => {
            const ownerDate = new Date(owner.createdAt);
            return ownerDate >= filterDate;
          });
          break;
        case "week":
          filterDate.setDate(now.getDate() - 7);
          filtered = filtered.filter((owner) => {
            const ownerDate = new Date(owner.createdAt);
            return ownerDate >= filterDate;
          });
          break;
        case "month":
          filterDate.setMonth(now.getMonth() - 1);
          filtered = filtered.filter((owner) => {
            const ownerDate = new Date(owner.createdAt);
            return ownerDate >= filterDate;
          });
          break;
        case "year":
          filterDate.setFullYear(now.getFullYear() - 1);
          filtered = filtered.filter((owner) => {
            const ownerDate = new Date(owner.createdAt);
            return ownerDate >= filterDate;
          });
          break;
        default:
          break;
      }
    }

    setFilteredData(filtered);
  }, [data, typeFilter, emailVerifiedFilter, profileCompleteFilter, searchQuery, dateFilter]);

  const fetchPropertyOwners = async () => {
    try {
      setLoading(true);
      const response = await superAdminAPI.getAllPropertyOwners(
        statusFilter !== "all" ? statusFilter : undefined
      );
      setData(response.data || []);
    } catch (error) {
      console.error("Error fetching property owners:", error);
    } finally {
      setLoading(false);
    }
  };

  const clearAllFilters = () => {
    setTypeFilter("all");
    setEmailVerifiedFilter("all");
    setProfileCompleteFilter("all");
    setSearchQuery("");
    setDateFilter("all");
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (typeFilter !== "all") count++;
    if (emailVerifiedFilter !== "all") count++;
    if (profileCompleteFilter !== "all") count++;
    if (searchQuery.trim()) count++;
    if (dateFilter !== "all") count++;
    return count;
  };

  const openMenu = ({ currentTarget }, owner) => {
    setMenu(currentTarget);
    setSelectedOwner(owner);
  };

  const closeMenu = () => {
    setMenu(null);
    // Don't clear selectedOwner here - it's needed for the dialog
  };

  const handleAction = (action) => {
    setMenu(null); // Close menu but keep selectedOwner
    setActionDialog({ open: true, action });
    setApprovalNotes("");
    setApprovalType(selectedOwner?.type || "Standard");
    if (action === "editTypeNote") {
      setEditType(selectedOwner?.type || "Standard");
      setEditNotes(selectedOwner?.notes || "");
    }
  };

  const handleSubmitAction = async () => {
    console.log("🟢 Frontend - handleSubmitAction called", {
      selectedOwner,
      actionDialog,
    });

    if (!selectedOwner) {
      console.error("🟢 Frontend - No selectedOwner!");
      alert("Error: No property owner selected. Please try again.");
      return;
    }

    if (!actionDialog.action) {
      console.error("🟢 Frontend - No action specified!");
      alert("Error: No action specified. Please try again.");
      return;
    }

    try {
      setSubmitting(true);
      const ownerId = selectedOwner._id || selectedOwner.id;
      console.log("🟢 Frontend - Submitting action:", {
        action: actionDialog.action,
        ownerId: ownerId,
        selectedOwner: selectedOwner,
      });

      if (!ownerId) {
        throw new Error("Property owner ID is missing");
      }

      let response;
      switch (actionDialog.action) {
        case "approve":
          console.log("🟢 Calling approvePropertyOwner API...");
          response = await superAdminAPI.approvePropertyOwner(
            selectedOwner._id || selectedOwner.id,
            {
              notes: approvalNotes,
              type: approvalType,
            }
          );
          console.log("🟢 Approve response:", response);
          break;
        case "editTypeNote":
          response = await superAdminAPI.updatePropertyOwner(
            selectedOwner._id || selectedOwner.id,
            {
              type: editType,
              notes: editNotes.trim(),
            }
          );
          break;
        case "suspend":
          console.log("🟢 Calling suspendPropertyOwner API...");
          response = await superAdminAPI.suspendPropertyOwner(
            selectedOwner._id || selectedOwner.id
          );
          console.log("🟢 Suspend response:", response);
          break;
        case "delete":
          console.log("🟢 Calling deletePropertyOwner API...");
          response = await superAdminAPI.deletePropertyOwner(selectedOwner._id || selectedOwner.id);
          console.log("🟢 Delete response:", response);
          break;
        default:
          return;
      }

      // Check if response is successful (either has success: true or has message indicating success)
      console.log("🟢 Frontend - Response check:", {
        response,
        hasSuccess: !!response?.success,
        hasMessage: !!response?.message,
        responseType: typeof response,
        responseKeys: response ? Object.keys(response) : [],
        responseStringified: JSON.stringify(response),
      });

      // Handle response - check for success indicator or message
      // Backend returns: { message: "...", owner: {...} }
      // Frontend adds: { ...response, success: true }
      const isSuccess =
        response &&
        (response.success === true ||
          response.message ||
          (response.owner && response.owner.status));

      if (isSuccess) {
        console.log("🟢 Frontend - Success! Refreshing data...");
        await fetchPropertyOwners();
        setActionDialog({ open: false, action: null });
        setApprovalNotes("");
        setApprovalType("Standard");
        setEditType("Standard");
        setEditNotes("");
        setSelectedOwner(null);
        setAlertMessage({
          type: "success",
          message: response.message || `Property owner ${actionDialog.action}d successfully`,
        });
        // Clear alert after 5 seconds
        setTimeout(() => setAlertMessage({ type: "", message: "" }), 5000);
      } else {
        console.error("🟢 Frontend - Unexpected response format:", response);
        throw new Error(response?.message || "Unexpected response format");
      }
    } catch (error) {
      console.error(`Error ${actionDialog.action}ing property owner:`, error);
      const errorMessage =
        error.message || error.data?.message || `Failed to ${actionDialog.action} property owner`;
      setAlertMessage({
        type: "error",
        message: errorMessage,
      });
      // Clear alert after 5 seconds
      setTimeout(() => setAlertMessage({ type: "", message: "" }), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Active":
        return "success";
      case "Pending":
        return "warning";
      case "Suspended":
        return "error";
      case "Rejected":
        return "error";
      default:
        return "secondary";
    }
  };

  const columns = [
    { Header: "Name", accessor: "fullName", width: "15%" },
    { Header: "Email", accessor: "email", width: "20%" },
    { Header: "Phone", accessor: "phone", width: "12%" },
    {
      Header: "Type",
      accessor: "type",
      width: "10%",
      Cell: ({ value }) => (
        <Chip
          label={value || "Standard"}
          color={value === "VIP" ? "warning" : "default"}
          size="small"
          sx={{ fontWeight: "bold" }}
        />
      ),
    },
    {
      Header: "Status",
      accessor: "status",
      width: "10%",
      Cell: ({ value }) => (
        <Chip
          label={value}
          color={getStatusColor(value)}
          size="small"
          sx={{ fontWeight: "bold" }}
        />
      ),
    },
    {
      Header: "Email Verified",
      accessor: "emailVerified",
      width: "10%",
      Cell: ({ value }) => (
        <Chip
          label={value ? "Verified" : "Not Verified"}
          color={value ? "success" : "warning"}
          size="small"
        />
      ),
    },
    {
      Header: "Profile",
      accessor: "profileComplete",
      width: "10%",
      Cell: ({ value }) => (
        <Chip
          label={value ? "Complete" : "Incomplete"}
          color={value ? "success" : "warning"}
          size="small"
          icon={<Icon>{value ? "check_circle" : "warning"}</Icon>}
        />
      ),
    },
    {
      Header: "Notes",
      accessor: "notes",
      width: "13%",
      Cell: ({ value }) => (
        <MDTypography
          variant="caption"
          color="text"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "150px",
            display: "block",
          }}
          title={value || ""}
        >
          {value ? (value.length > 30 ? `${value.substring(0, 30)}...` : value) : "-"}
        </MDTypography>
      ),
    },
    { Header: "Actions", accessor: "actions", width: "10%", align: "center" },
  ];

  const rows = filteredData.map((owner) => ({
    fullName: owner.fullName,
    email: owner.email,
    phone: owner.phone || "N/A",
    type: owner.type || "Standard",
    status: owner.status || "Pending",
    emailVerified: owner.emailVerified || false,
    profileComplete: isProfileComplete(owner),
    notes: owner.notes || "",
    actions: (
      <MDBox display="flex" justifyContent="center">
        <IconButton
          onClick={(e) => openMenu(e, owner)}
          size="small"
          sx={{ color: "text.secondary" }}
        >
          <Icon>more_vert</Icon>
        </IconButton>
      </MDBox>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
          <MDBox>
            <MDTypography variant="h4" fontWeight="bold">
              Property Owners Management
            </MDTypography>
            <MDTypography variant="body2" color="text">
              Manage property owner accounts, approve pending requests, and view details
            </MDTypography>
          </MDBox>
        </MDBox>

        {/* Analytics Statistics Cards */}
        <MDBox
          mb={3}
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(5, 1fr)",
            },
            gap: 3,
          }}
        >
          <MDBox>
            <ComplexStatisticsCard
              color="info"
              icon="people"
              title="Total Owners"
              count={data.length}
              percentage={{
                color: "success",
                amount: `${data.filter((o) => o.status === "Active").length}`,
                label: "active",
              }}
              sx={{ width: "100%", minHeight: "180px", height: "100%" }}
            />
          </MDBox>
          <MDBox>
            <ComplexStatisticsCard
              color="warning"
              icon="star"
              title="VIP Owners"
              count={data.filter((o) => o.type === "VIP").length}
              percentage={{
                color: "info",
                amount: `${
                  data.length > 0
                    ? Math.round((data.filter((o) => o.type === "VIP").length / data.length) * 100)
                    : 0
                }%`,
                label: "of total",
              }}
              sx={{ width: "100%", minHeight: "180px", height: "100%" }}
            />
          </MDBox>
          <MDBox>
            <ComplexStatisticsCard
              color="success"
              icon="verified"
              title="Verified Emails"
              count={data.filter((o) => o.emailVerified).length}
              percentage={{
                color: "success",
                amount: `${
                  data.length > 0
                    ? Math.round((data.filter((o) => o.emailVerified).length / data.length) * 100)
                    : 0
                }%`,
                label: "verified",
              }}
              sx={{ width: "100%", minHeight: "180px", height: "100%" }}
            />
          </MDBox>
          <MDBox>
            <ComplexStatisticsCard
              color="primary"
              icon="block"
              title="Suspended"
              count={data.filter((o) => o.status === "Suspended").length}
              percentage={{
                color: "warning",
                amount: `${
                  data.length > 0
                    ? Math.round(
                        (data.filter((o) => o.status === "Suspended").length / data.length) * 100
                      )
                    : 0
                }%`,
                label: "of total",
              }}
              sx={{ width: "100%", minHeight: "180px", height: "100%" }}
            />
          </MDBox>
          <MDBox>
            <ComplexStatisticsCard
              color="dark"
              icon="check_circle"
              title="Complete Profiles"
              count={data.filter((o) => isProfileComplete(o)).length}
              percentage={{
                color: "success",
                amount: `${
                  data.length > 0
                    ? Math.round(
                        (data.filter((o) => isProfileComplete(o)).length / data.length) * 100
                      )
                    : 0
                }%`,
                label: "complete",
              }}
              sx={{ width: "100%", minHeight: "180px", height: "100%" }}
            />
          </MDBox>
        </MDBox>

        {/* Alert Messages */}
        {alertMessage.message && (
          <MDBox mb={3}>
            <Alert
              severity={alertMessage.type}
              onClose={() => setAlertMessage({ type: "", message: "" })}
            >
              {alertMessage.message}
            </Alert>
          </MDBox>
        )}

        {/* Filters Section */}
        <Card sx={{ mb: 3 }}>
          <MDBox p={2}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <MDBox display="flex" alignItems="center" gap={1}>
                <MDTypography variant="h6" fontWeight="medium">
                  Filters & Search
                </MDTypography>
                {getActiveFiltersCount() > 0 && (
                  <Chip
                    label={getActiveFiltersCount()}
                    color="primary"
                    size="small"
                    sx={{ fontWeight: "bold" }}
                  />
                )}
              </MDBox>
              <MDBox display="flex" gap={1}>
                {getActiveFiltersCount() > 0 && (
                  <MDButton
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={clearAllFilters}
                    startIcon={<Icon>clear_all</Icon>}
                  >
                    Clear Filters
                  </MDButton>
                )}
                <MDButton
                  variant="text"
                  color="info"
                  size="small"
                  onClick={() => setShowFilters(!showFilters)}
                  endIcon={<Icon>{showFilters ? "expand_less" : "expand_more"}</Icon>}
                >
                  {showFilters ? "Hide Filters" : "Show Filters"}
                </MDButton>
              </MDBox>
            </MDBox>

            {/* Status Filter - Always Visible */}
            <MDBox mb={2}>
              <MDTypography variant="caption" color="text.secondary" mb={1} display="block">
                Status
              </MDTypography>
              <MDBox display="flex" gap={1} flexWrap="wrap">
                <Chip
                  label={`All (${data.length})`}
                  onClick={() => setStatusFilter("all")}
                  color={statusFilter === "all" ? "primary" : "default"}
                  icon={<Icon>list</Icon>}
                  sx={{ cursor: "pointer" }}
                />
                <Chip
                  label={`Active (${data.filter((o) => o.status === "Active").length})`}
                  onClick={() => setStatusFilter("Active")}
                  color={statusFilter === "Active" ? "primary" : "default"}
                  icon={<Icon>check_circle</Icon>}
                  sx={{ cursor: "pointer" }}
                />
                <Chip
                  label={`Suspended (${data.filter((o) => o.status === "Suspended").length})`}
                  onClick={() => setStatusFilter("Suspended")}
                  color={statusFilter === "Suspended" ? "primary" : "default"}
                  icon={<Icon>block</Icon>}
                  sx={{ cursor: "pointer" }}
                />
                <Chip
                  label={`Rejected (${data.filter((o) => o.status === "Rejected").length})`}
                  onClick={() => setStatusFilter("Rejected")}
                  color={statusFilter === "Rejected" ? "primary" : "default"}
                  icon={<Icon>close</Icon>}
                  sx={{ cursor: "pointer" }}
                />
              </MDBox>
            </MDBox>

            {/* Advanced Filters - Collapsible */}
            <Collapse in={showFilters}>
              <Grid container spacing={2}>
                {/* Search */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Search"
                    placeholder="Search by name, email, phone, or notes..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon>search</Icon>
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setSearchQuery("")} edge="end">
                            <Icon fontSize="small">clear</Icon>
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ height: "40px" }}
                  />
                </Grid>

                {/* Type Filter */}
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Owner Type</InputLabel>
                    <Select
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      label="Owner Type"
                      sx={{ height: "40px" }}
                    >
                      <MenuItem value="all">All Types</MenuItem>
                      <MenuItem value="Standard">Standard</MenuItem>
                      <MenuItem value="VIP">VIP</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Email Verification Filter */}
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Email Verification</InputLabel>
                    <Select
                      value={emailVerifiedFilter}
                      onChange={(e) => setEmailVerifiedFilter(e.target.value)}
                      label="Email Verification"
                      sx={{ height: "40px" }}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="verified">Verified</MenuItem>
                      <MenuItem value="unverified">Not Verified</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Profile Completion Filter */}
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Profile Status</InputLabel>
                    <Select
                      value={profileCompleteFilter}
                      onChange={(e) => setProfileCompleteFilter(e.target.value)}
                      label="Profile Status"
                      sx={{ height: "40px" }}
                    >
                      <MenuItem value="all">All Profiles</MenuItem>
                      <MenuItem value="complete">Complete</MenuItem>
                      <MenuItem value="incomplete">Incomplete</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Date Filter */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Registration Date</InputLabel>
                    <Select
                      value={dateFilter}
                      onChange={(e) => setDateFilter(e.target.value)}
                      label="Registration Date"
                      sx={{ height: "40px" }}
                    >
                      <MenuItem value="all">All Time</MenuItem>
                      <MenuItem value="today">Today</MenuItem>
                      <MenuItem value="week">Last 7 Days</MenuItem>
                      <MenuItem value="month">Last 30 Days</MenuItem>
                      <MenuItem value="year">Last Year</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Active Filters Summary */}
                {getActiveFiltersCount() > 0 && (
                  <Grid item xs={12}>
                    <MDBox
                      p={1.5}
                      borderRadius={1}
                      bgcolor="info.lighter"
                      display="flex"
                      alignItems="center"
                      gap={1}
                      flexWrap="wrap"
                    >
                      <MDTypography variant="caption" fontWeight="bold" color="info.main">
                        Active Filters:
                      </MDTypography>
                      {typeFilter !== "all" && (
                        <Chip
                          label={`Type: ${typeFilter}`}
                          size="small"
                          onDelete={() => setTypeFilter("all")}
                          color="info"
                        />
                      )}
                      {emailVerifiedFilter !== "all" && (
                        <Chip
                          label={`Email: ${
                            emailVerifiedFilter === "verified" ? "Verified" : "Not Verified"
                          }`}
                          size="small"
                          onDelete={() => setEmailVerifiedFilter("all")}
                          color="info"
                        />
                      )}
                      {profileCompleteFilter !== "all" && (
                        <Chip
                          label={`Profile: ${
                            profileCompleteFilter === "complete" ? "Complete" : "Incomplete"
                          }`}
                          size="small"
                          onDelete={() => setProfileCompleteFilter("all")}
                          color="info"
                        />
                      )}
                      {searchQuery && (
                        <Chip
                          label={`Search: "${searchQuery.substring(0, 20)}${
                            searchQuery.length > 20 ? "..." : ""
                          }"`}
                          size="small"
                          onDelete={() => setSearchQuery("")}
                          color="info"
                        />
                      )}
                      {dateFilter !== "all" && (
                        <Chip
                          label={`Date: ${
                            dateFilter.charAt(0).toUpperCase() + dateFilter.slice(1)
                          }`}
                          size="small"
                          onDelete={() => setDateFilter("all")}
                          color="info"
                        />
                      )}
                    </MDBox>
                  </Grid>
                )}
              </Grid>
            </Collapse>

            {/* Results Summary */}
            <MDBox mt={2} display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="caption" color="text.secondary">
                Showing {filteredData.length} of {data.length} property owners
              </MDTypography>
              {getActiveFiltersCount() > 0 && (
                <MDButton
                  variant="text"
                  color="secondary"
                  size="small"
                  onClick={clearAllFilters}
                  startIcon={<Icon>refresh</Icon>}
                >
                  Reset Filters
                </MDButton>
              )}
            </MDBox>
          </MDBox>
        </Card>

        {/* Property Owners Table */}
        <Card>
          {loading ? (
            <MDBox p={6} display="flex" justifyContent="center">
              <CircularProgress />
            </MDBox>
          ) : (
            <DataTable
              table={{ columns, rows }}
              isSorted={false}
              entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25, 50] }}
              showTotalEntries={true}
              noEndBorder
            />
          )}
        </Card>

        {/* Action Menu */}
        <Menu
          anchorEl={menu}
          open={Boolean(menu)}
          onClose={closeMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem
            onClick={() => {
              if (selectedOwner) {
                navigate(
                  `/super-admin/property-owners/profile/${selectedOwner._id || selectedOwner.id}`
                );
              }
              closeMenu();
            }}
          >
            <Icon sx={{ mr: 1 }}>person</Icon> View Profile
          </MenuItem>
          <MenuItem onClick={() => handleAction("editTypeNote")}>
            <Icon sx={{ mr: 1 }}>edit</Icon> Edit type & note
          </MenuItem>
          <Divider />
          {selectedOwner?.status === "Active" && (
            <MenuItem onClick={() => handleAction("suspend")}>
              <Icon sx={{ mr: 1 }}>block</Icon> Suspend
            </MenuItem>
          )}
          {selectedOwner?.status === "Suspended" && (
            <MenuItem onClick={() => handleAction("approve")}>
              <Icon sx={{ mr: 1 }}>check</Icon> Reactivate
            </MenuItem>
          )}
          {selectedOwner?.status === "Rejected" && (
            <MenuItem disabled>
              <Icon sx={{ mr: 1 }}>info</Icon> No actions available for rejected accounts
            </MenuItem>
          )}
          <Divider />
          <MenuItem onClick={() => handleAction("delete")} sx={{ color: "error.main" }}>
            <Icon sx={{ mr: 1 }}>delete</Icon> Delete Account
          </MenuItem>
        </Menu>

        {/* Action Dialog */}
        <Dialog
          open={actionDialog.open}
          onClose={() => {
            if (!submitting) {
              setActionDialog({ open: false, action: null });
              setSelectedOwner(null);
              setApprovalNotes("");
              setApprovalType("Standard");
              setEditType("Standard");
              setEditNotes("");
            }
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {actionDialog.action === "approve"
              ? selectedOwner?.status === "Suspended"
                ? "Reactivate Property Owner"
                : "Approve Property Owner"
              : actionDialog.action === "editTypeNote"
              ? "Edit type & note"
              : actionDialog.action === "delete"
              ? "Delete Property Owner Account"
              : "Suspend Property Owner"}
          </DialogTitle>
          <DialogContent>
            {selectedOwner && (
              <MDBox>
                {actionDialog.action === "delete" ? (
                  <MDBox>
                    <Alert severity="error" sx={{ mb: 2 }}>
                      <MDTypography variant="body2" fontWeight="bold">
                        Warning: This action cannot be undone!
                      </MDTypography>
                      <MDTypography variant="body2">
                        Deleting this account will permanently remove:
                      </MDTypography>
                      <MDBox component="ul" sx={{ mt: 1, pl: 2 }}>
                        <MDTypography component="li" variant="body2">
                          The property owner account
                        </MDTypography>
                        <MDTypography component="li" variant="body2">
                          All associated properties and their media files
                        </MDTypography>
                        <MDTypography component="li" variant="body2">
                          All uploaded documents (ID card, photos, official documents)
                        </MDTypography>
                      </MDBox>
                    </Alert>
                    <MDBox p={2} borderRadius="lg" bgColor="grey.100" mb={2}>
                      <MDTypography variant="subtitle2" fontWeight="medium">
                        {selectedOwner.fullName}
                      </MDTypography>
                      <MDTypography variant="body2" color="text">
                        {selectedOwner.email}
                      </MDTypography>
                    </MDBox>
                    <MDTypography variant="body2" color="text" fontWeight="medium">
                      Are you absolutely sure you want to delete this property owner account?
                    </MDTypography>
                  </MDBox>
                ) : actionDialog.action === "editTypeNote" ? (
                  <MDBox>
                    <MDBox p={2} borderRadius="lg" bgColor="grey.100" mb={2}>
                      <MDTypography variant="subtitle2" fontWeight="medium">
                        {selectedOwner.fullName}
                      </MDTypography>
                      <MDTypography variant="body2" color="text">
                        {selectedOwner.email}
                      </MDTypography>
                    </MDBox>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={editType}
                        onChange={(e) => setEditType(e.target.value)}
                        label="Type"
                        sx={{ height: "40px" }}
                        disabled={submitting}
                      >
                        <MenuItem value="Standard">Standard</MenuItem>
                        <MenuItem value="VIP">VIP</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Note"
                      placeholder="Add or edit notes for this property owner..."
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                      disabled={submitting}
                    />
                  </MDBox>
                ) : (
                  <>
                    <MDTypography variant="body2" color="text" mb={2}>
                      {actionDialog.action === "approve"
                        ? selectedOwner.status === "Suspended"
                          ? "You are about to reactivate this property owner account."
                          : "You are about to approve this property owner account."
                        : "You are about to suspend this property owner account."}
                    </MDTypography>
                    <MDBox p={2} borderRadius="lg" bgColor="grey.100" mb={2}>
                      <MDTypography variant="subtitle2" fontWeight="medium">
                        {selectedOwner.fullName}
                      </MDTypography>
                      <MDTypography variant="body2" color="text">
                        {selectedOwner.email}
                      </MDTypography>
                    </MDBox>
                    {actionDialog.action === "approve" && (
                      <MDBox>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                          <InputLabel>Owner Type</InputLabel>
                          <Select
                            value={approvalType}
                            onChange={(e) => setApprovalType(e.target.value)}
                            label="Owner Type"
                            sx={{ height: "40px" }}
                            disabled={submitting}
                          >
                            <MenuItem value="Standard">Standard</MenuItem>
                            <MenuItem value="VIP">VIP</MenuItem>
                          </Select>
                        </FormControl>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          label="Notes (Optional)"
                          placeholder="Add any notes about this approval..."
                          value={approvalNotes}
                          onChange={(e) => setApprovalNotes(e.target.value)}
                          disabled={submitting}
                        />
                      </MDBox>
                    )}
                  </>
                )}
              </MDBox>
            )}
          </DialogContent>
          <DialogActions>
            <MDButton
              onClick={() => {
                setActionDialog({ open: false, action: null });
                setSelectedOwner(null);
                setApprovalNotes("");
                setApprovalType("Standard");
                setEditType("Standard");
                setEditNotes("");
              }}
              disabled={submitting}
            >
              Cancel
            </MDButton>
            <MDButton
              onClick={handleSubmitAction}
              variant="gradient"
              color={
                actionDialog.action === "delete"
                  ? "error"
                  : actionDialog.action === "approve" || actionDialog.action === "editTypeNote"
                  ? "success"
                  : "warning"
              }
              disabled={submitting}
            >
              {submitting
                ? "Processing..."
                : actionDialog.action === "approve"
                ? selectedOwner?.status === "Suspended"
                  ? "Reactivate"
                  : "Approve"
                : actionDialog.action === "editTypeNote"
                ? "Save"
                : actionDialog.action === "delete"
                ? "Delete Account"
                : "Suspend"}
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default PropertyOwnersManagement;
